import { getControlItem } from '../utils/helpers'
import { CONTROL_CLASS } from '../utils/videoplayer-constants'
import { Controls } from './controls'

const PLAYBACK_SPEED_LABEL = 'Playback Speed'
const ARIA_LABEL_ATTR = 'aria-label'
const ARIA_EXPANDED_ATTR = 'aria-expanded'

export class Speed extends Controls {
  private readonly speeds: number[] = [0.5, 1, 1.25, 1.5, 2.0, 3.0]
  private currentIndex = 1
  private dropdownElement: HTMLDivElement
  private buttonElement: HTMLButtonElement
  private options: HTMLButtonElement[] = []
  private buttonLabelElement: HTMLSpanElement
  private optionsContainerElement: HTMLDivElement
  private hideOnInactiveTimeout?: number
  private documentClickListener?: (event: MouseEvent) => void

  constructor(element: HTMLElement, videoPlayer: HTMLVideoElement, wrapperElement: HTMLDivElement) {
    super(element, videoPlayer, wrapperElement)
    this.init()
  }

  private init() {
    this.videoPlayer.playbackRate = this.speeds[this.currentIndex]
    this.renderSpeedControl()
    this.registerUserActiveObserver()
  }

  private renderSpeedControl() {
    const wrapper = getControlItem({
      parent: {
        element: 'div',
        classes: ['cmp-video-wrapper__controls--control', 'cmp-video-wrapper__controls-speed-wrapper'],
      },
    }) as HTMLDivElement

    this.buttonElement = getControlItem({
      parent: {
        element: 'button',
        classes: [CONTROL_CLASS, 'cmp-video-wrapper__controls--speed'],
      },
      children: [
        {
          element: 'span',
          classes: ['cmp-video-wrapper__controls--speed-icon'],
        },
        {
          element: 'span',
          classes: ['cmp-video-wrapper__controls--speed-label'],
          text: this.formatSpeedLabel(this.speeds[this.currentIndex]),
        },
      ],
      eventListeners: ['click'],
      callback: () => this.toggleDropdown(),
    }) as HTMLButtonElement

    this.buttonLabelElement = this.buttonElement.querySelector('.cmp-video-wrapper__controls--speed-label') as HTMLSpanElement

    this.buttonElement.setAttribute('aria-haspopup', 'true')
    this.buttonElement.setAttribute(ARIA_EXPANDED_ATTR, 'false')
    this.buttonElement.setAttribute(ARIA_LABEL_ATTR, this.getButtonAriaLabel())

    if (this.buttonLabelElement) {
      this.buttonLabelElement.setAttribute('aria-live', 'polite')
    }

    this.buttonElement.addEventListener('keydown', event => this.onButtonKeyDown(event))

    this.dropdownElement = document.createElement('div')
    this.dropdownElement.classList.add('cmp-video-wrapper__controls-speed-dropdown')
    this.dropdownElement.style.display = 'none'

    const header = getControlItem({
      parent: {
        element: 'div',
        classes: ['cmp-video-wrapper__controls--speed-header'],
      },
      children: [
        {
          element: 'span',
          classes: [CONTROL_CLASS, 'cmp-video-wrapper__controls--speed-header-close'],
          text: PLAYBACK_SPEED_LABEL,
        },
      ],
      eventListeners: ['click'],
      callback: () => this.closeDropdown(),
    }) as HTMLDivElement

    this.optionsContainerElement = document.createElement('div')
    this.optionsContainerElement.classList.add('cmp-video-wrapper__controls--speed-options')
    this.optionsContainerElement.setAttribute('role', 'menu')
    this.optionsContainerElement.setAttribute(ARIA_LABEL_ATTR, PLAYBACK_SPEED_LABEL)

    this.dropdownElement.appendChild(header)
    this.dropdownElement.appendChild(this.optionsContainerElement)

    this.speeds.forEach(speed => {
      const option = getControlItem({
        parent: {
          element: 'button',
          classes: [CONTROL_CLASS, 'cmp-video-wrapper__controls--speed-option'],
          attributes: {
            ['data-speed']: `${speed}`,
          },
          text: this.formatSpeedLabel(speed),
        },
        eventListeners: ['click'],
        callback: () => this.selectSpeed(speed),
      }) as HTMLButtonElement

      option.addEventListener('keydown', event => this.onOptionKeyDown(event))

      this.options.push(option)
      this.optionsContainerElement.appendChild(option)
    })

    wrapper.appendChild(this.buttonElement)
    wrapper.appendChild(this.dropdownElement)
    this.controlBarSectionElement.appendChild(wrapper)

    this.updateActiveOption()
  }

  private registerUserActiveObserver() {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-user-active') {
          const isUserActive = this.wrapperElement.dataset.userActive

          if (isUserActive === 'false') {
            if (this.hideOnInactiveTimeout) {
              window.clearTimeout(this.hideOnInactiveTimeout)
            }

            this.hideOnInactiveTimeout = window.setTimeout(() => {
              this.closeDropdown()
              this.hideOnInactiveTimeout = undefined
            }, 500)
          } else {
            if (this.hideOnInactiveTimeout) {
              window.clearTimeout(this.hideOnInactiveTimeout)
              this.hideOnInactiveTimeout = undefined
            }
          }
        }
      })
    })

    observer.observe(this.wrapperElement, { attributes: true, attributeFilter: ['data-user-active'] })
  }

  private toggleDropdown() {
    const isHidden = this.dropdownElement.style.display === 'none'

    if (isHidden) {
      this.dropdownElement.style.display = 'block'
      this.buttonElement.setAttribute(ARIA_EXPANDED_ATTR, 'true')

      if (!this.documentClickListener) {
        this.documentClickListener = (event: MouseEvent) => {
          const target = event.target as Node | null

          if (!target) {
            return
          }

          const clickInsideVideoWrapper = this.wrapperElement.contains(target)

          if (!clickInsideVideoWrapper) {
            this.closeDropdown()
          }
        }

        document.addEventListener('click', this.documentClickListener)
      }
    } else {
      this.closeDropdown()
    }
  }

  private selectSpeed(speed: number) {
    this.currentIndex = this.speeds.indexOf(speed)
    this.videoPlayer.playbackRate = speed
    const label = this.formatSpeedLabel(speed)
    if (this.buttonLabelElement) {
      this.buttonLabelElement.innerText = label
    } else {
      this.buttonElement.innerText = label
    }
    this.buttonElement.setAttribute(ARIA_LABEL_ATTR, this.getButtonAriaLabel())
    this.updateActiveOption()
  }

  private closeDropdown() {
    if (this.dropdownElement.style.display === 'none') {
      return
    }

    this.dropdownElement.style.display = 'none'
    this.buttonElement.setAttribute(ARIA_EXPANDED_ATTR, 'false')

    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener)
      this.documentClickListener = undefined
    }
  }

  private updateActiveOption() {
    const currentSpeed = this.speeds[this.currentIndex]

    this.options.forEach(option => {
      const optionSpeed = parseFloat(option.dataset.speed || '')
      const isActive = optionSpeed === currentSpeed

      option.classList.toggle('cmp-video-wrapper__controls--speed-option--active', isActive)
      option.setAttribute('aria-checked', isActive ? 'true' : 'false')
      option.setAttribute('role', 'menuitemradio')
      option.tabIndex = isActive ? 0 : -1
    })
  }

  private formatSpeedLabel(speed: number): string {
    if (speed === 1) {
      return 'Normal'
    }

    const formatted = Number.isInteger(speed) ? speed.toFixed(1) : speed.toString()

    return `${formatted}x`
  }

  private getButtonAriaLabel(): string {
    const label = this.formatSpeedLabel(this.speeds[this.currentIndex])

    return `${PLAYBACK_SPEED_LABEL}, ${label}`
  }

  private onButtonKeyDown(event: KeyboardEvent) {
    const key = event.key

    if (key === 'ArrowDown' || key === 'Down' || key === 'ArrowUp' || key === 'Up') {
      event.preventDefault()

      if (this.dropdownElement.style.display === 'none') {
        this.toggleDropdown()
      }

      this.focusCurrentOption()
    }
  }

  private onOptionKeyDown(event: KeyboardEvent) {
    const target = event.currentTarget as HTMLButtonElement | null

    if (!target) {
      return
    }

    const index = this.options.indexOf(target)

    if (index === -1) {
      return
    }

    const key = event.key

    if (key === 'ArrowDown' || key === 'Down') {
      event.preventDefault()

      const nextIndex = Math.min(this.speeds.length - 1, index + 1)

      if (nextIndex !== index) {
        this.selectSpeed(this.speeds[nextIndex])
        this.options[nextIndex].focus()
      }
    } else if (key === 'ArrowUp' || key === 'Up') {
      event.preventDefault()

      const prevIndex = Math.max(0, index - 1)

      if (prevIndex !== index) {
        this.selectSpeed(this.speeds[prevIndex])
        this.options[prevIndex].focus()
      }
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      this.selectSpeed(this.speeds[index])
      this.closeDropdown()
      this.buttonElement.focus()
    } else if (key === 'Escape' || key === 'Esc') {
      event.preventDefault()
      this.closeDropdown()
      this.buttonElement.focus()
    }
  }

  private focusCurrentOption() {
    const currentOption = this.options[this.currentIndex]

    if (currentOption) {
      currentOption.focus()
    }
  }
}
